import { makeAutoObservable } from 'mobx';
import lmsIntegrationAgent from '../services/api/lmsIntegrationAgent';
import { ILinkedIntegration } from '../routes/courseDashboard/features/dashboardViews/PublishCourseView';
import { ELMSType, ILMSUpdateLinkItem, IWordpressCourseListItem } from '../services/models/LMSLinkModel';

export interface IMentorkitLMSCourseList {
  CourseId: number;
  Name: string;
}

export interface ITargetCourseListItem {
  id: number;
  name: string;
  lmsCourseName: string;
  identifierLMSCourse: string;
  publishURL: string | null;
}

interface ITargetCourseList {
  lmsTypeId: ELMSType | null;
  lmsCustomerId: number;
  courseList: ITargetCourseListItem[];
}

export default class PublishStore {
  targetCourseList: ITargetCourseList = { lmsTypeId: null, lmsCustomerId: 0, courseList: [] };
  isTargetCourseListLoaded = false;

  constructor() {
    makeAutoObservable(this);
  }

  private mapMentorkitCourseList = (courseList: IMentorkitLMSCourseList[]): ITargetCourseListItem[] => {
    return (courseList ?? []).map((course) => ({
      id: course.CourseId,
      name: course.Name,
      lmsCourseName: course.Name,
      identifierLMSCourse: course.CourseId.toString(),
      publishURL: null,
    }));
  };

  private mapWordpressCourseListItem = (course: IWordpressCourseListItem): ITargetCourseListItem | null => {
    const identifier = typeof course.identifierLMSCourse === 'string' ? course.identifierLMSCourse : '';
    const parsedId = Number.parseInt(identifier, 10);
    const id = Number.isFinite(parsedId) && parsedId > 0 ? parsedId : course.id;
    if (!Number.isFinite(id) || id <= 0) return null;

    const name = course.lmsCourseName || course.name || `Course ${id}`;

    return {
      id,
      name,
      lmsCourseName: course.lmsCourseName || course.name || name,
      identifierLMSCourse: identifier || id.toString(),
      publishURL: course.publishURL ?? null,
    };
  };

  private getWordpressCourseList = async (lmsCustomerId: number): Promise<ITargetCourseListItem[]> => {
    const MAX_PAGES = 50;

    const items: ITargetCourseListItem[] = [];
    const seenIdentifiers = new Set<string>();

    let page = 1;
    let totalPages: number | null = null;

    while (page <= MAX_PAGES && (totalPages === null || page <= totalPages)) {
      const response = await lmsIntegrationAgent.Wordpress.GetCourseList(lmsCustomerId, page);

      if (typeof response.totalPages === 'number') {
        totalPages = response.totalPages;
      }

      const responseItems = response.items ?? [];

      for (const course of responseItems) {
        const mapped = this.mapWordpressCourseListItem(course);
        if (!mapped) continue;

        if (seenIdentifiers.has(mapped.identifierLMSCourse)) continue;
        seenIdentifiers.add(mapped.identifierLMSCourse);
        items.push(mapped);
      }

      if (responseItems.length === 0) break;

      if (totalPages === null) {
        const perPage = typeof response.perPage === 'number' ? response.perPage : 20;
        if (responseItems.length < perPage) break;
      }

      page += 1;
    }

    return items;
  };

  getTargetCourseList = async (lmsTypeId: ELMSType, lmsCustomerId: number) => {
    if (this.targetCourseList.lmsCustomerId === lmsCustomerId && this.targetCourseList.lmsTypeId === lmsTypeId) return;
    this.isTargetCourseListLoaded = false;

    try {
      let courseList: ITargetCourseListItem[] = [];

      if (lmsTypeId === ELMSType.Mentorkit_LMS) {
        const mentorkitCourseList = await lmsIntegrationAgent.MentorkitLms.GetCourseList(lmsCustomerId);
        courseList = this.mapMentorkitCourseList(mentorkitCourseList ?? []);
      } else if (lmsTypeId === ELMSType.WordPress) {
        courseList = await this.getWordpressCourseList(lmsCustomerId);
      }

      this.targetCourseList = { lmsTypeId, lmsCustomerId, courseList };
    } catch (e) {
      console.error('Failed to load target course list', e);
      this.targetCourseList = { lmsTypeId, lmsCustomerId, courseList: [] };
    } finally {
      this.isTargetCourseListLoaded = true;
    }
  };

  updateLMSLink = async (aTargetCourseID: number, aIntegration: ILinkedIntegration, aCreatorCourseName: string) => {
    if (!aIntegration.courseLMSLink) {
      throw new Error('Missing LMS link');
    }

    let lmsCourseName = aCreatorCourseName;
    let identifierLMSCourse = '0';

    if (aTargetCourseID !== 0) {
      const targetCourse = this.targetCourseList.courseList.find((x) => x.id === aTargetCourseID);
      if (!targetCourse) {
        throw new Error(`Cant find target course ${aTargetCourseID}`);
      }

      lmsCourseName = targetCourse.lmsCourseName || targetCourse.name;
      identifierLMSCourse = targetCourse.identifierLMSCourse || targetCourse.id.toString();
    }

    aIntegration.courseLMSLink.lmsCourseName = lmsCourseName;
    aIntegration.courseLMSLink.identifierLMSCourse = identifierLMSCourse;

    const updateLMSLink: ILMSUpdateLinkItem = {
      lmsCustomerId: aIntegration.courseLMSLink.lmsCustomerId,
      lmsCustomerCourseId: aIntegration.courseLMSLink.lmsCustomerCourseId,
      lmsCourseName,
      identifierLMSCourse,
    };

    const response = await lmsIntegrationAgent.CourseLMSLink.updateLMSLink(updateLMSLink);
    if (response?.publishURL) {
      aIntegration.courseLMSLink.publishURL = response.publishURL;
    }
    return response;
  };

  getPublishLogs = async (aCourseID: number) => {
    return await lmsIntegrationAgent.CourseLMSLink.getPublishLogsByCourse(aCourseID);
  };
}
